import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Toast, { type ToastState } from "../../../ui/Toast/Toast";
import { guardianApi } from "../../../../lib/guardian";
import Button from "../../../ui/Button/Button";
import PageActionBar from "../../../ui/Page/PageActionBar";
import PageBackButton from "../../../ui/Page/PageBackButton";
import PageFormSection from "../../../ui/Page/PageFormSection";
import PageSectionCard from "../../../ui/Page/PageSectionCard";
import PageShell from "../../../ui/Page/PageShell";
import { useAuth } from "../../../../hooks/useAuth";
import { stripePayoutExplainer } from "../../../../constants/stripeCopy";
import type { GuardianTeenDetailResponse } from "../../../../types/guardianTypes";
import type { StripePayoutProfileResponse } from "../../../../types/stripeTypes";
import { apiErrorToMessage } from "../../../../utils/ApiUtils";
import TeenProfileBusinessListingApplicationsSection from "./TeenProfileBusinessListingApplicationsSection";
import TeenProfileGigRequestsSection from "./TeenProfileGigRequestsSection";
import TeenProfileInfoCard from "./TeenProfileInfoCard";

type TeenProfileTab = "overview" | "gigRequests" | "businessListings" | "payout";

const parseTeenProfileTab = (value: string | null): TeenProfileTab => {
  if (value === "gigs") {
    return "gigRequests";
  }
  if (value === "overview" || value === "gigRequests" || value === "businessListings" || value === "payout") {
    return value;
  }

  return "gigRequests";
};

type GuardianPayoutSetupState = {
  tone: "ready" | "attention" | "neutral";
  statusLabel: string;
  nextStepTitle: string;
  nextStepDescription: string;
  actionLabel: string;
  canStart: boolean;
  disabledReason: string | null;
};

const payoutTrustItems = [
  {
    title: "Verifies the gig poster",
    description: (teenName: string) => `MyTeenGig keeps a record of who posted the job and hired ${teenName}.`,
    icon: "shield",
  },
  {
    title: "Tracks the job and payment",
    description: () => "The agreed-upon gig details and payout amount are documented before work starts.",
    icon: "document",
  },
  {
    title: "Confirms payment",
    description: () => "Funds are collected through Stripe and released after the gig is completed.",
    icon: "payment",
  },
  {
    title: "More protection for your teen",
    description: () => "A payment record helps prevent non-payment and gives support something to review if there is ever a question.",
    icon: "people",
  },
] as const;

const payoutSetupSteps = [
  {
    title: "Open Stripe",
    description: "Use the secure setup button from this page or from the email your teen sent.",
  },
  {
    title: "Add payout details",
    description: "Stripe collects the parent or guardian information needed so when your teen completes gigs, the money goes to this account.",
  },
  {
    title: "Return to MyTeenGig",
    description: "When Stripe sends you back, your teen's completed gig payouts will go to the account you entered, and they'll be ready for future paid gigs.",
  },
] as const;

const payoutStatusToneClassNames: Record<GuardianPayoutSetupState["tone"], string> = {
  ready: "border-emerald-200 bg-emerald-50 text-emerald-800",
  attention: "border-amber-200 bg-amber-50 text-amber-900",
  neutral: "border-myteengig-border bg-white text-myteengig-text-primary",
};

const payoutStatusDotClassNames: Record<GuardianPayoutSetupState["tone"], string> = {
  ready: "bg-emerald-500",
  attention: "bg-amber-500",
  neutral: "bg-myteengig-primary",
};

const formatPayoutStatusLabel = (status: StripePayoutProfileResponse["onboardingStatus"] | undefined) => {
  if (status === "READY") {
    return "Ready for paid gigs";
  }
  if (status === "ONBOARDING_REQUIRED") {
    return "Needs secure setup";
  }
  return "Not started";
};

const buildGuardianPayoutSetupState = (
  payoutProfile: StripePayoutProfileResponse | null,
  selectedGuardianMatchesViewer: boolean,
  teenName: string,
): GuardianPayoutSetupState => {
  if (!payoutProfile) {
    return {
      tone: "neutral",
      statusLabel: "Checking payout setup",
      nextStepTitle: "Loading payout details",
      nextStepDescription: "This page will show the next setup step once the teen profile finishes loading.",
      actionLabel: "Set up Stripe payouts",
      canStart: false,
      disabledReason: "Payout setup is still loading.",
    };
  }

  if (payoutProfile.payoutOwnerType === "TEEN") {
    return {
      tone: "neutral",
      statusLabel: "Teen-managed payouts",
      nextStepTitle: "No parent action needed",
      nextStepDescription: `${teenName} can complete or update their own Stripe payout setup from teen settings.`,
      actionLabel: "Set up Stripe payouts",
      canStart: false,
      disabledReason: "This teen is managing payouts directly.",
    };
  }

  if (payoutProfile.requiresGuardianSelection) {
    return {
      tone: "attention",
      statusLabel: "Waiting for teen selection",
      nextStepTitle: `${teenName} needs to choose a payout guardian`,
      nextStepDescription: `Ask ${teenName} to open Gig Payouts in teen settings, select the parent or guardian who should handle payouts, and send the setup email. A payout guardian is the one who will set up the account that will receive payments when ${teenName} completes a gig.`,
      actionLabel: "Set up Stripe payouts",
      canStart: false,
      disabledReason: "The teen has not selected a payout guardian yet.",
    };
  }

  if (!selectedGuardianMatchesViewer) {
    const selectedGuardian = payoutProfile.guardianName ?? "another guardian";
    return {
      tone: "neutral",
      statusLabel: "Assigned to another guardian",
      nextStepTitle: "Only the selected guardian can continue",
      nextStepDescription: `${selectedGuardian} needs to sign in and complete Stripe setup for ${teenName}.`,
      actionLabel: "Set up Stripe payouts",
      canStart: false,
      disabledReason: "Only the selected payout guardian can open Stripe onboarding.",
    };
  }

  if (payoutProfile.onboardingStatus === "READY") {
    return {
      tone: "ready",
      statusLabel: "Ready for paid gigs",
      nextStepTitle: "Payout setup is ready",
      nextStepDescription: `No action is required right now. You can reopen Stripe if the payout account details need to be reviewed or updated.`,
      actionLabel: "Review Stripe setup",
      canStart: true,
      disabledReason: null,
    };
  }

  if (payoutProfile.detailsSubmitted) {
    return {
      tone: "attention",
      statusLabel: "Needs one more Stripe step",
      nextStepTitle: "Finish the remaining Stripe step",
      nextStepDescription: "Open Stripe to review the account and complete any missing payout or verification requirements.",
      actionLabel: "Continue secure setup",
      canStart: true,
      disabledReason: null,
    };
  }

  if (payoutProfile.guardianOnboardingEmailSentAt) {
    return {
      tone: "attention",
      statusLabel: "Setup email sent",
      nextStepTitle: "Complete the secure Stripe setup",
      nextStepDescription: "Use the email link or this page to open Stripe and add the payout account details.",
      actionLabel: "Continue secure setup",
      canStart: true,
      disabledReason: null,
    };
  }

  return {
    tone: "attention",
    statusLabel: formatPayoutStatusLabel(payoutProfile.onboardingStatus),
    nextStepTitle: "Start the secure setup",
    nextStepDescription: "Open Stripe to add the parent or guardian payout account so paid gigs can be funded.",
    actionLabel: "Start secure setup",
    canStart: true,
    disabledReason: null,
  };
};

const PayoutActionIcon = () => (
  <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 10H16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M11 5L16 10L11 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PayoutTrustIcon = ({ icon }: { icon: (typeof payoutTrustItems)[number]["icon"] }) => {
  if (icon === "shield") {
    return (
      <svg aria-hidden="true" className="h-6 w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 3L19 6V11C19 15.6 16.1 19.1 12 21C7.9 19.1 5 15.6 5 11V6L12 3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      </svg>
    );
  }

  if (icon === "document") {
    return (
      <svg aria-hidden="true" className="h-6 w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 3H15L19 7V21H7V3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M15 3V8H19" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M10 12H16M10 16H15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  if (icon === "payment") {
    return (
      <svg aria-hidden="true" className="h-6 w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 7V17M9.5 15C9.5 16.2 10.6 17 12 17C13.4 17 14.5 16.2 14.5 15C14.5 13.9 13.7 13.4 12 12.9C10.3 12.4 9.5 11.9 9.5 10.8C9.5 9.7 10.6 9 12 9C13.4 9 14.5 9.7 14.5 10.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg aria-hidden="true" className="h-6 w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="15" cy="8" r="3" stroke="currentColor" strokeWidth="1.8" />
      <path d="M4 19C4.6 15.8 6.4 14 9 14C11.6 14 13.4 15.8 14 19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M10 19C10.6 15.8 12.4 14 15 14C17.6 14 19.4 15.8 20 19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
};

const AdultGuardianTeenProfileWorkspace = () => {
  const { teenId } = useParams<{ teenId: string }>();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [teenDetail, setTeenDetail] = useState<GuardianTeenDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [decidingKey, setDecidingKey] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>(null);
  const [isStartingPayoutOnboarding, setIsStartingPayoutOnboarding] = useState(false);
  const payoutSectionRef = useRef<HTMLDivElement | null>(null);
  const lastPayoutScrollKeyRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadTeenDetail = async () => {
      if (!teenId) {
        setError("Teen ID is required.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await guardianApi.getTeenDetail(teenId);
        if (!cancelled) {
          setTeenDetail(response);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(apiErrorToMessage(requestError, "Failed to load teen details."));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadTeenDetail();
    return () => {
      cancelled = true;
    };
  }, [teenId]);

  useEffect(() => {
    const payoutState = searchParams.get("stripePayout");
    if (!payoutState || !teenId) {
      return;
    }

    const reloadTeenDetail = async () => {
      try {
        const response = await guardianApi.getTeenDetail(teenId);
        setTeenDetail(response);
        setToast({
          tone: payoutState === "return" ? "success" : "info",
          message:
            payoutState === "return"
              ? "Returned from Stripe. Refreshing payout setup status now."
              : "Stripe asked for one more onboarding step. Continue the payout setup when you are ready.",
        });
      } catch (requestError) {
        setError(apiErrorToMessage(requestError, "Failed to refresh the payout setup status."));
      }
    };

    void reloadTeenDetail();

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("stripePayout");
    nextParams.delete("payoutSetup");
    nextParams.set("tab", "payout");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, teenId]);

  const onDecideRequest = async (requestId: string, decision: "APPROVED" | "REJECTED") => {
    if (!teenId || !user?.id) {
      setError("Guardian identity is required.");
      return;
    }

    const key = `${requestId}-${decision}`;
    setDecidingKey(key);
    setError(null);

    try {
      await guardianApi.createApproval(requestId, {
        guardianUserId: user.id,
        decision,
        message: null,
      });

      const refreshed = await guardianApi.getTeenDetail(teenId);
      setTeenDetail(refreshed);
      setToast({
        tone: "success",
        message: decision === "APPROVED" ? "Gig request approved." : "Gig request declined.",
      });
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Failed to update guardian approval.");
      setError(message);
      setToast({ tone: "error", message });
    } finally {
      setDecidingKey(null);
    }
  };

  const onStartPayoutOnboarding = async () => {
    if (!teenId || !teenDetail || isStartingPayoutOnboarding) {
      return;
    }

    setIsStartingPayoutOnboarding(true);
    setError(null);
    try {
      const response = await guardianApi.createTeenPayoutOnboardingLink(teenId);
      setTeenDetail({
        ...teenDetail,
        payoutProfile: response.profile,
      });
      window.location.assign(response.url);
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Unable to start Stripe payout onboarding.");
      setError(message);
      setToast({ tone: "error", message });
    } finally {
      setIsStartingPayoutOnboarding(false);
    }
  };

  const payoutProfile = teenDetail?.payoutProfile ?? null;
  const selectedGuardianMatchesViewer = Boolean(
    payoutProfile?.guardianUserId && user?.id && payoutProfile.guardianUserId === user.id,
  );
  const payoutSetupRequested = searchParams.get("payoutSetup") === "1" || Boolean(searchParams.get("stripePayout"));
  const activeTab = payoutSetupRequested ? "payout" : parseTeenProfileTab(searchParams.get("tab"));
  const payoutTeenName = teenDetail?.firstName?.trim() || "this teen";
  const hasPendingApprovals = (teenDetail?.summary.pendingGuardianApprovals ?? 0) > 0;
  const payoutNeedsAttention =
    payoutProfile?.payoutOwnerType === "GUARDIAN"
    && selectedGuardianMatchesViewer
    && payoutProfile?.onboardingStatus !== "READY";
  const payoutSetupState = buildGuardianPayoutSetupState(
    payoutProfile,
    selectedGuardianMatchesViewer,
    payoutTeenName,
  );

  const setActiveTab = (tab: TeenProfileTab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("payoutSetup");
    nextParams.delete("stripePayout");
    if (tab === "gigRequests") {
      nextParams.delete("tab");
    } else {
      nextParams.set("tab", tab);
    }
    setSearchParams(nextParams);
  };

  useEffect(() => {
    if (loading || error || !teenDetail || activeTab !== "payout") {
      return;
    }

    const scrollKey = [
      teenId ?? "",
      searchParams.get("tab") ?? "",
      searchParams.get("payoutSetup") ?? "",
      searchParams.get("stripePayout") ?? "",
    ].join("|");
    if (lastPayoutScrollKeyRef.current === scrollKey) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const targetElement = payoutSectionRef.current;
      if (!targetElement) {
        return;
      }

      const targetTop = targetElement.getBoundingClientRect().top + window.scrollY - 24;
      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: "smooth",
      });
      lastPayoutScrollKeyRef.current = scrollKey;
    }, 80);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [activeTab, error, loading, searchParams, teenDetail, teenId]);

  const tabButtons: Array<{
    key: TeenProfileTab;
    label: string;
    summary: string;
    badge?: number;
  }> = [
    {
      key: "overview",
      label: "Profile Details",
      summary: "Teen profile details, guardian context, and account summary",
    },
    {
      key: "gigRequests",
      label: "Gig Requests",
      summary: "Requests, approvals, and scheduled work",
      badge: teenDetail?.summary.pendingGuardianApprovals ?? 0,
    },
    {
      key: "businessListings",
      label: "Business Listings",
      summary: "Applications submitted to business job listings",
      badge: teenDetail?.businessListingApplications.length ?? 0,
    },
    {
      key: "payout",
      label: "Guardian Payout Setup",
      summary: "Stripe onboarding for paid gigs",
      badge: payoutNeedsAttention ? 1 : 0,
    },
  ];

  return (
    <PageShell width="7xl">
      <Toast
        isOpen={toast !== null}
        message={toast?.message ?? ""}
        tone={toast?.tone ?? "info"}
        onClose={() => setToast(null)}
      />
      <PageBackButton fallbackTo="/adult/guardian/teens" label="Back to My Teens" className="mb-4" />

      {loading ? <p className="mt-4 text-myteengig-text-muted">Loading teen details...</p> : null}
      {error ? <p className="mt-4 text-sm font-medium text-red-600">{error}</p> : null}

      {!loading && !error && teenDetail ? (
        <div className="grid gap-6">
          <PageSectionCard className="p-6">
            <div className="relative flex flex-col gap-4 sm:min-h-[88px]">
              <div className="mx-auto text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Teen Profile</p>
                <h1 className="mt-2 text-3xl font-semibold tracking-tight text-myteengig-text-primary">
                  {teenDetail.firstName} {teenDetail.lastName}
                </h1>
                <p className="mt-2 text-sm text-myteengig-text-muted">{teenDetail.email}</p>
              </div>
              <div className="flex flex-wrap justify-center gap-2 sm:absolute sm:right-0 sm:top-0 sm:justify-end">
                {hasPendingApprovals ? (
                  <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900">
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                    Needs attention
                  </span>
                ) : null}
                {payoutNeedsAttention ? (
                  <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900">
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                    Payout setup needed
                  </span>
                ) : null}
              </div>
            </div>
          </PageSectionCard>

          <PageSectionCard className="p-5">
            <PageActionBar align="center">
              {tabButtons.map((tab) => (
                <Button
                  key={tab.key}
                  type="button"
                  variant={activeTab === tab.key ? "primary" : "outline"}
                  className="w-full sm:min-w-[220px] sm:w-auto"
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                  {tab.badge && tab.badge > 0 ? ` · ${tab.badge}` : ""}
                </Button>
              ))}
            </PageActionBar>

            <div className="mt-5 rounded-2xl border border-dashed border-myteengig-border bg-myteengig-background/60 px-4 py-3 text-center">
              <p className="text-sm font-semibold text-myteengig-text-primary">
                {tabButtons.find((tab) => tab.key === activeTab)?.label}
              </p>
              <p className="text-sm text-myteengig-text-muted">
                {tabButtons.find((tab) => tab.key === activeTab)?.summary}
              </p>
            </div>
          </PageSectionCard>

          {activeTab === "overview" ? (
            <div className="grid gap-6 xl:grid-cols-[minmax(0,0.88fr)_minmax(0,1.12fr)]">
              <TeenProfileInfoCard teen={teenDetail} />
              <PageSectionCard className="p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Account Summary</p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-myteengig-text-primary">Teen activity summary</h2>
                <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <div className="rounded-[1.5rem] border border-myteengig-border bg-myteengig-background p-4">
                    <p className="text-sm font-semibold text-myteengig-text-primary">Completed gigs</p>
                    <p className="mt-3 text-4xl font-bold tracking-tight text-myteengig-text-primary">{teenDetail.summary.completedGigs}</p>
                  </div>
                  <div className="rounded-[1.5rem] border border-myteengig-border bg-myteengig-background p-4">
                    <p className="text-sm font-semibold text-myteengig-text-primary">Scheduled gigs</p>
                    <p className="mt-3 text-4xl font-bold tracking-tight text-myteengig-text-primary">{teenDetail.summary.scheduledGigs}</p>
                  </div>
                  <div className="rounded-[1.5rem] border border-myteengig-border bg-myteengig-background p-4">
                    <p className="text-sm font-semibold text-myteengig-text-primary">Request-stage gigs</p>
                    <p className="mt-3 text-4xl font-bold tracking-tight text-myteengig-text-primary">{teenDetail.summary.requestStageGigs}</p>
                  </div>
                  <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50 p-4">
                    <p className="text-sm font-semibold text-amber-900">Pending guardian approvals</p>
                    <p className="mt-3 text-4xl font-bold tracking-tight text-amber-900">{teenDetail.summary.pendingGuardianApprovals}</p>
                  </div>
                  <div className="rounded-[1.5rem] border border-myteengig-border bg-myteengig-background p-4">
                    <p className="text-sm font-semibold text-myteengig-text-primary">Active guardians</p>
                    <p className="mt-3 text-4xl font-bold tracking-tight text-myteengig-text-primary">{teenDetail.summary.activeGuardians}</p>
                  </div>
                  <div className="rounded-[1.5rem] border border-myteengig-border bg-myteengig-background p-4">
                    <p className="text-sm font-semibold text-myteengig-text-primary">Total earned</p>
                    <p className="mt-3 text-4xl font-bold tracking-tight text-myteengig-text-primary">
                      ${((teenDetail.summary.totalEarnedCents ?? 0) / 100).toFixed(2)}
                    </p>
                  </div>
                  <div className="rounded-[1.5rem] border border-myteengig-border bg-myteengig-background p-4">
                    <p className="text-sm font-semibold text-myteengig-text-primary">Average rating</p>
                    <p className="mt-3 text-4xl font-bold tracking-tight text-myteengig-text-primary">
                      {teenDetail.summary.totalWorkerReviews > 0 ? teenDetail.summary.averageWorkerRating.toFixed(1) : "New"}
                    </p>
                  </div>
                  <div className="rounded-[1.5rem] border border-myteengig-border bg-myteengig-background p-4">
                    <p className="text-sm font-semibold text-myteengig-text-primary">Worker reviews</p>
                    <p className="mt-3 text-4xl font-bold tracking-tight text-myteengig-text-primary">{teenDetail.summary.totalWorkerReviews}</p>
                  </div>
                  <div className="rounded-[1.5rem] border border-myteengig-border bg-myteengig-background p-4">
                    <p className="text-sm font-semibold text-myteengig-text-primary">Reviews given</p>
                    <p className="mt-3 text-4xl font-bold tracking-tight text-myteengig-text-primary">{teenDetail.summary.reviewsGiven}</p>
                  </div>
                </div>
              </PageSectionCard>
            </div>
          ) : null}

          {activeTab === "gigRequests" ? (
            <TeenProfileGigRequestsSection
              requests={teenDetail.gigRequests}
              decidingKey={decidingKey}
              onDecideRequest={onDecideRequest}
              onOpenPayoutSetup={() => setActiveTab("payout")}
            />
          ) : null}

          {activeTab === "businessListings" ? (
            <TeenProfileBusinessListingApplicationsSection
              applications={teenDetail.businessListingApplications}
            />
          ) : null}

          {activeTab === "payout" ? (
            <div ref={payoutSectionRef}>
              <PageFormSection
                eyebrow="Paid Gig Payouts"
                title={`Set up payouts for ${payoutTeenName}`}
                description={`A parent or guardian completes one secure Stripe setup so ${payoutTeenName} can be paid for gigs through MyTeenGig.`}
              >
                <div className="rounded-lg border border-myteengig-border bg-myteengig-background/70 p-5">
                  <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${payoutStatusToneClassNames[payoutSetupState.tone]}`}>
                    <span className={`h-2 w-2 rounded-full ${payoutStatusDotClassNames[payoutSetupState.tone]}`} />
                    {payoutSetupState.statusLabel}
                  </span>
                  <h3 className="mt-4 text-xl font-semibold text-myteengig-text-primary">{payoutSetupState.nextStepTitle}</h3>
                  <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">{payoutSetupState.nextStepDescription}</p>

                  <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Button
                      type="button"
                      onClick={() => void onStartPayoutOnboarding()}
                      disabled={isStartingPayoutOnboarding || !payoutSetupState.canStart}
                      className="inline-flex w-full items-center justify-center gap-2 sm:w-auto"
                    >
                      <PayoutActionIcon />
                      {isStartingPayoutOnboarding ? "Opening Stripe..." : payoutSetupState.actionLabel}
                    </Button>
                    {payoutSetupState.disabledReason ? (
                      <p className="text-sm text-myteengig-text-muted">{payoutSetupState.disabledReason}</p>
                    ) : (
                      <p className="text-sm text-myteengig-text-muted">You will return to this page after Stripe.</p>
                    )}
                  </div>
                </div>

                <div className="mt-5 rounded-lg border border-myteengig-border bg-white p-5">
                  <p className="text-sm font-semibold text-myteengig-text-primary">What happens next</p>
                  <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    {payoutSetupSteps.map((step, index) => (
                      <div key={step.title} className="rounded-lg border border-myteengig-border bg-myteengig-background/70 p-4">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-myteengig-primary text-xs font-semibold text-white">
                          {index + 1}
                        </span>
                        <p className="mt-3 text-sm font-semibold text-myteengig-text-primary">{step.title}</p>
                        <p className="mt-1 text-sm leading-6 text-myteengig-text-muted">{step.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-5 rounded-lg border border-myteengig-border bg-white p-5">
                  <p className="text-sm font-semibold text-myteengig-text-primary">Why Stripe?</p>
                  <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">{stripePayoutExplainer}</p>

                  <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    {payoutTrustItems.map((item) => (
                      <div key={item.title} className="rounded-lg border border-myteengig-border bg-myteengig-background/70 p-4 text-center">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                          <PayoutTrustIcon icon={item.icon} />
                        </div>
                        <p className="mt-3 text-sm font-semibold text-myteengig-text-primary">{item.title}</p>
                        <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">{item.description(payoutTeenName)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </PageFormSection>
            </div>
          ) : null}
        </div>
      ) : null}
    </PageShell>
  );
};

export default AdultGuardianTeenProfileWorkspace;
